import { useCallback, useMemo, useRef, useEffect, useState } from 'react'
import styled from '@emotion/styled'
import Dropdown from '@groovehq/internal-design-system/lib/components/Dropdown/Dropdown'
import KeyboardNavigator from 'components/KeyboardNavigator'
import { SUI, Dropdown as SUIDropdown } from 'shared/ui'
import { emptyArr, getLength } from 'util/arrays'
import { propFunc } from 'util/functions'
import RecipientItem from './RecipientItem'
import DropdownMenu from './DropdownMenu'

const internalDesignSystemDropdownOffset = [4, 5]

const RecipientDropdown = ({ sui, ...rest }) => {
  if (sui) return <LegacySUIDropdown {...rest} />
  return <InternalDesignSystemDropdown {...rest} />
}

const InternalDesignSystemDropdown = ({
  className,
  disabled,
  isSearching,
  lastFilteredSize,
  onClick,
  onClose,
  recipients,
  trigger,
  input,
}) => {
  const [visible, setVisible] = useState(false)
  // Active index from onSelect (KeyboardNavigator) always get the previous index when pressiong enter key
  // maybe it has to do with enter handler for SearchBox
  // Track the activated item by onActivate in DropdownMenu instead
  const activatedRecipientIdRef = useRef()
  const handleSelect = useCallback(
    () => {
      if (activatedRecipientIdRef.current) {
        onClick(activatedRecipientIdRef.current)
      }
    },
    [onClick]
  )

  const handleVisibilityChange = useCallback(
    v => {
      setVisible(v)
      if (!v) {
        if (onClose) onClose()
      }
    },
    [onClose]
  )

  useEffect(
    () => {
      setVisible(
        !!(
          !disabled &&
          (getLength(recipients) > 0 || (isSearching && lastFilteredSize > 0))
        )
      )
    },
    [disabled, recipients, isSearching, lastFilteredSize]
  )

  return (
    <KeyboardNavigator
      focusElement={input}
      // Enter handler
      onSelect={handleSelect}
      className="grui h-auto"
    >
      <Dropdown
        className={className}
        strategy="fixed"
        portal="#overlays"
        overlay={
          <DropdownMenu
            onSelect={handleSelect}
            activatedRecipientIdRef={activatedRecipientIdRef}
            recipients={recipients}
            lastFilteredSize={lastFilteredSize}
          />
        }
        tabIndex="0"
        onVisibleChange={handleVisibilityChange}
        visible={visible}
        offset={internalDesignSystemDropdownOffset}
        // Make sure dropdown won't focus its children and blur the input in SearchBox
        isNavByArrowsDisabled
        menuItemIconHidden
      >
        <div>{trigger}</div>
      </Dropdown>
    </KeyboardNavigator>
  )
}

const LegacySUIDropdown = ({
  className,
  disabled,
  isSearching,
  onClick,
  onClose,
  onBlur,
  recipients,
  trigger,
  upward,
}) => {
  const suiOptions = useMemo(
    () => {
      if (!isSearching) {
        return recipients.filter(e => !!e).map(r => ({
          key: r.id,
          content: <RecipientItem recipient={r} />,
          onClick: propFunc(onClick, r.id),
          value: r.id,
        }))
      }

      return emptyArr
    },
    [isSearching, recipients, onClick]
  )

  return (
    <SUI>
      <SUIDropdown
        className={className}
        icon={null}
        onBlur={onBlur}
        onClose={onClose}
        open={!disabled}
        openOnFocus={false}
        options={suiOptions}
        scrolling
        trigger={trigger}
        upward={upward}
        tabIndex="-1"
      />
    </SUI>
  )
}

export default styled(RecipientDropdown)`
  &&& {
    width: ${props => (props.sui ? '100%' : undefined)};
    align-items: center;
    color: ${props => props.theme.color.monochrome.black};
    cursor: pointer;
    /* flex/inline-flex will cut off items in InternalDesignSystemDropdown*/
    display: ${props => (props.sui ? 'inline-flex' : 'block')};

    .dropdown {
      &.icon {
        /* SUI override for a bigger caret thats closer to icon */
        font-size: ${props => props.theme.fontSize.small} !important;
        margin-left: ${props => props.theme.spacing['4']} !important;
      }
    }

    [role='menuitem']:not(.highlighted):hover {
      background-color: transparent;
    }

    & > .menu.menu.menu {
      max-width: 350px;
      min-width: 0 !important;
    }
  }
`
